import {
  Box,
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import ClearIcon from '@material-ui/icons/Clear';
import { SnackbarContext } from 'components';
import * as React from 'react';
import {
  LightspeedMappingService,
  LightspeedProductionInstructionGroup,
  LightspeedShopId,
  LightspeedShopState,
  LightspeedStateMenuItem,
} from 'services';

interface Props {
  shopId: LightspeedShopId;
  state: LightspeedShopState | null;
  piCatalog: LightspeedProductionInstructionGroup[];
  onChanged: () => void | Promise<void>;
}

interface ModOption {
  modifierId: string;
  groupName: string;
  label: string; // "Loaded Fries Sauce › Kewpie Mayo"
}

const useStyles = makeStyles((theme) => ({
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1.5),
    '& > * + *': { marginLeft: theme.spacing(2) },
  },
  search: { minWidth: 280 },
  emptyHint: {
    color: theme.palette.text.secondary,
    padding: theme.spacing(2),
  },
  ok: { background: '#e8f5e9' },
  drift: { background: '#ffebee' },
  rowSku: {
    background: '#e3f2fd',
  },
}));

export const ModifiersTab: React.FC<Props> = ({
  shopId,
  state,
  piCatalog,
  onChanged,
}) => {
  const classes = useStyles();
  const snackbar = React.useContext(SnackbarContext);

  const [filter, setFilter] = React.useState('');
  const [showOnlyUnmapped, setShowOnlyUnmapped] = React.useState(false);
  const [savingId, setSavingId] = React.useState<number | null>(null);

  const options: ModOption[] = React.useMemo(() => {
    const out: ModOption[] = [];
    piCatalog.forEach((group) => {
      const groupName = group.productionInstructionGroupName || '(no group)';
      (group.productionInstructionList || []).forEach((mod) => {
        if (mod.ikentooModifierId === undefined || mod.ikentooModifierId === null) return;
        const id = String(mod.ikentooModifierId);
        out.push({
          modifierId: id,
          groupName,
          label: `${groupName} › ${mod.instruction}  ·  id ${id}`,
        });
      });
    });
    return out;
  }, [piCatalog]);

  const validIds = React.useMemo(
    () => new Set(options.map((o) => o.modifierId)),
    [options],
  );

  const filtered = React.useMemo(() => {
    if (!state) return [];
    const q = filter.trim().toLowerCase();
    return state.menuItems.filter((mi) => {
      if (showOnlyUnmapped && (mi.lightspeedSku || mi.lightspeedModifierId)) {
        return false;
      }
      if (q === '') return true;
      return (
        mi.title.toLowerCase().includes(q) ||
        mi.categoryName.toLowerCase().includes(q) ||
        mi.sectionName.toLowerCase().includes(q)
      );
    });
  }, [state, filter, showOnlyUnmapped]);

  async function saveMod(
    item: LightspeedStateMenuItem,
    modifierId: string | null,
  ) {
    try {
      setSavingId(item.id);
      await LightspeedMappingService.updateMenuItem(item.id, {
        shopId,
        lightspeed_modifier_id: modifierId,
      });
      snackbar.success(
        modifierId
          ? `Linked "${item.title}" → modifier ${modifierId}`
          : `Cleared "${item.title}"`,
      );
      await onChanged();
    } catch (e) {
      snackbar.error(e);
    } finally {
      setSavingId(null);
    }
  }

  if (!state) {
    return (
      <Typography className={classes.emptyHint}>
        Loading menu items for shop {shopId}…
      </Typography>
    );
  }

  const catalogReady = piCatalog.length > 0;

  return (
    <Box>
      <Box className={classes.toolbar}>
        <TextField
          variant="outlined"
          size="small"
          className={classes.search}
          placeholder="Search menu items…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
        <label>
          <input
            type="checkbox"
            checked={showOnlyUnmapped}
            onChange={(e) => setShowOnlyUnmapped(e.target.checked)}
          />{' '}
          Show unmapped only
        </label>
        <Box flex={1} />
        <Typography variant="body2" color="textSecondary">
          {state.menuItems.length} items in shop {shopId} ·{' '}
          {catalogReady
            ? `${options.length} modifiers available across ${piCatalog.length} groups`
            : 'Paste a production-instructions catalog first'}
        </Typography>
      </Box>

      {!catalogReady && (
        <Paper className={classes.emptyHint} elevation={0}>
          No production-instructions catalog has been pasted for this shop
          yet. Use the <strong>Paste catalogs</strong> tab.
        </Paper>
      )}

      {catalogReady && (
        <Paper>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell style={{ width: '30%' }}>Item</TableCell>
                <TableCell style={{ width: '20%' }}>Category</TableCell>
                <TableCell style={{ width: '10%' }}>Status</TableCell>
                <TableCell>Lightspeed modifier</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((mi) => {
                const currentId = mi.lightspeedModifierId;
                const hasSku = !!mi.lightspeedSku;
                const driftBadge =
                  currentId && !validIds.has(currentId) ? (
                    <Chip
                      size="small"
                      label="Drift — modifier missing"
                      className={classes.drift}
                    />
                  ) : currentId ? (
                    <Chip size="small" label="Mapped" className={classes.ok} />
                  ) : hasSku ? (
                    <Chip size="small" label="SKU" />
                  ) : (
                    <Chip size="small" label="Unmapped" />
                  );

                const selectedOpt = currentId
                  ? options.find((o) => o.modifierId === currentId) ?? {
                      modifierId: currentId,
                      groupName: '(unknown)',
                      label: `${currentId} (not in latest catalog)`,
                    }
                  : null;

                return (
                  <TableRow
                    key={mi.id}
                    className={hasSku ? classes.rowSku : ''}
                  >
                    <TableCell>
                      <strong>{mi.title}</strong>
                      <br />
                      <Typography
                        variant="caption"
                        color="textSecondary"
                      >
                        id {mi.id}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{mi.sectionName}</Typography>
                      <Typography variant="caption" color="textSecondary">
                        {mi.categoryName}
                      </Typography>
                    </TableCell>
                    <TableCell>{driftBadge}</TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                        <Autocomplete
                          style={{ flex: 1 }}
                          size="small"
                          options={options}
                          groupBy={(o) => o.groupName}
                          getOptionLabel={(o) => o.label}
                          getOptionSelected={(a, b) =>
                            a.modifierId === b.modifierId
                          }
                          value={selectedOpt}
                          disabled={savingId === mi.id}
                          onChange={(_e: any, picked: ModOption | null) => {
                            const id = picked ? picked.modifierId : null;
                            if (id === currentId) return;
                            saveMod(mi, id);
                          }}
                          renderInput={(p) => (
                            <TextField
                              {...p}
                              variant="outlined"
                              placeholder={
                                hasSku
                                  ? 'Setting a modifier will clear the SKU link'
                                  : 'Pick a Lightspeed production-instruction modifier'
                              }
                            />
                          )}
                        />
                        {currentId && (
                          <IconButton
                            size="small"
                            disabled={savingId === mi.id}
                            onClick={() => saveMod(mi, null)}
                            aria-label="Clear mapping"
                          >
                            <ClearIcon />
                          </IconButton>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Typography className={classes.emptyHint}>
                      No items match the current filter.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      )}
    </Box>
  );
};
